import { useEffect, useId, useState } from 'react'
import { Dialog } from './Dialog'
import { RoomThumb } from './RoomThumb'
import type { Me } from '../lib/me'
import { useHubLang } from '../lib/i18n'
import { CONTESTS } from '../lib/hubCopy'
import { messageOf } from '../lib/errors'
import { searchMentionable, type MentionablePerson } from '../lib/mentions'
import { contestWinDefault } from '../lib/photoRoom'
import {
  confirmResults,
  loadGallery,
  proposeVotePlaces,
  saveContest,
  setAnnounce,
  startMonthly,
  swapIfTied,
  type ConfirmOutcome,
  type Contest,
  type ContestKind,
  type ContestTexts,
  type GalleryEntry,
} from '../lib/photoContests'

/**
 * 146 (CMP-4), rebuilt by 147 (CMP-6). Organising and confirming contests.
 *
 * ContestForm — create or change a contest, or start the standing monthly
 * one (photo.contest, "Contest organiser"). Words in English (required) and
 * Spanish (one row per locale, rule 6); how winners are chosen; the dates;
 * places and judges' awards with their stars; the judges by name. A month of
 * the monthly contest takes its dates from the series, so the form does not
 * offer them, and says so.
 *
 * ConfirmResults — a named judge confirms, once voting (or, judged, entries)
 * has closed. On a vote the count decides; the judge orders photos that are
 * level and chooses the judges' award from the photos the vote did not place.
 * It is FINAL, and the confirm says what that does — stars, bells, the hub,
 * and every individual vote deleted.
 */

export function ContestForm({
  contest,
  monthly,
  tenantId,
  me,
  onDone,
  onCancel,
  bare = false,
}: {
  contest: Contest | null
  monthly: boolean
  tenantId: string | null
  me: Me
  /** After a save. Also after Cancel when `onCancel` is not given. */
  onDone: () => Promise<void>
  onCancel?: () => void
  /** In the Manage side panel (10 Oct): the panel's title names the form, so
   *  the form draws no heading and no box of its own. */
  bare?: boolean
}) {
  const lang = useHubLang()
  const t = CONTESTS[lang]
  const isMonth = !!contest?.seriesId
  const plus = (d: string, n: number) => {
    const [y, m, dd] = d.split('-').map(Number)
    return new Date(Date.UTC(y, m - 1, dd + n)).toISOString().slice(0, 10)
  }
  const today = new Date().toISOString().slice(0, 10)
  const [kind, setKind] = useState<ContestKind>(contest?.kind ?? 'vote')
  const [texts, setTexts] = useState<ContestTexts>(
    contest?.texts ?? (monthly ? { 'en-CA': { title: 'Photo of the month', objective: null, prize: null }, 'es-CL': { title: 'Foto del mes', objective: null, prize: null } } : {}),
  )
  const [startsOn, setStartsOn] = useState(contest?.startsOn ?? today)
  const [endsOn, setEndsOn] = useState(contest?.endsOn ?? plus(today, 14))
  const [voteEndsOn, setVoteEndsOn] = useState(contest?.voteEndsOn ?? plus(today, 21))
  const [photosSince, setPhotosSince] = useState(contest?.photosSince ?? contest?.startsOn ?? today)
  const [tz, setTz] = useState(contest?.timezone ?? me.timezone ?? 'America/Vancouver')
  const [placeStars, setPlaceStars] = useState<number[]>(contest?.places.filter((p) => p.award === 'place').map((p) => p.stars) ?? [5000])
  const [awardStars, setAwardStars] = useState<number[]>(
    contest ? contest.places.filter((p) => p.award === 'judges').map((p) => p.stars) : monthly ? [2500] : [],
  )
  const [judges, setJudges] = useState<{ personId: string; name: string }[]>(
    contest?.judges ?? [{ personId: me.personId, name: me.displayName }],
  )
  const [votesPerPerson, setVotesPerPerson] = useState(contest?.votesPerPerson ?? 1)
  const [maxEntries, setMaxEntries] = useState(contest?.maxEntries ?? 3)
  // 151: null is the whole of the next month — the default (David, 10 Oct).
  const [voteDays, setVoteDays] = useState<number | null>(null)
  const [announce, setAnnounceOn] = useState(contest?.announceInFeed ?? false)
  const [query, setQuery] = useState('')
  const [found, setFound] = useState<MentionablePerson[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (contest || !tenantId) return
    contestWinDefault(tenantId)
      .then((n) => setPlaceStars((s) => (s.length === 1 && s[0] === 5000 && n > 0 ? [n] : s)))
      .catch(() => undefined)
  }, [contest, tenantId])

  useEffect(() => {
    const q = query.trim()
    if (q.length < 2) {
      setFound([])
      return
    }
    let live = true
    searchMentionable(q, 8)
      .then((page) => live && setFound(page.people))
      .catch(() => live && setFound([]))
    return () => {
      live = false
    }
  }, [query])

  const words = (loc: 'en-CA' | 'es-CL') => texts[loc] ?? { title: '', objective: null, prize: null }
  const setWords = (loc: 'en-CA' | 'es-CL', field: 'title' | 'objective' | 'prize', v: string) =>
    setTexts((cur) => ({ ...cur, [loc]: { ...words(loc), ...(cur[loc] ?? {}), [field]: v } }))

  async function submit() {
    setBusy(true)
    setError(null)
    try {
      if (monthly) {
        await startMonthly({
          texts,
          timezone: tz,
          voteDays,
          placeStars,
          judgesAwardStars: awardStars,
          judges: judges.map((j) => j.personId),
          votesPerPerson,
        })
      } else {
        const id = await saveContest({
          id: contest?.id ?? null,
          kind,
          texts,
          startsOn,
          endsOn,
          voteEndsOn: kind === 'vote' ? voteEndsOn : null,
          timezone: tz,
          photosSince: isMonth ? null : photosSince,
          placeStars,
          judgesAwardStars: kind === 'vote' ? awardStars : [],
          judges: judges.map((j) => j.personId),
          votesPerPerson,
          maxEntries: isMonth ? null : maxEntries,
        })
        if (!isMonth && announce !== (contest?.announceInFeed ?? false)) await setAnnounce(id || (contest?.id as string), announce)
      }
      await onDone()
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  const starsList = (
    label: (i: number) => string,
    list: number[],
    set: (f: (cur: number[]) => number[]) => void,
  ) =>
    list.map((s, i) => (
      <label key={i}>
        {t.starsFor(label(i + 1))}
        <input
          type="number"
          min={0}
          max={10000}
          step={50}
          value={s}
          onChange={(e) => set((cur) => cur.map((x, j) => (j === i ? Math.max(0, Math.min(10000, Number(e.target.value) || 0)) : x)))}
        />
      </label>
    ))

  return (
    <form
      className={bare ? 'contests__form contests__form--bare' : 'contests__form'}
      onSubmit={(e) => {
        e.preventDefault()
        void submit()
      }}
    >
      {!bare && <h2 className="contests__title">{monthly ? t.startMonthly : contest ? t.edit : t.newContest}</h2>}

      {!monthly && !isMonth && (
        <fieldset className="contests__places">
          <legend>{t.formKind}</legend>
          <label className="contests__pick">
            <input type="radio" name="kind" checked={kind === 'vote'} onChange={() => setKind('vote')} /> {t.kindVote}
          </label>
          <label className="contests__pick">
            <input type="radio" name="kind" checked={kind === 'judged'} onChange={() => setKind('judged')} /> {t.kindJudged}
          </label>
        </fieldset>
      )}

      {(['en-CA', 'es-CL'] as const).map((loc) => (
        <fieldset key={loc} className="contests__places">
          <legend>{loc === 'en-CA' ? t.formWordsEn : t.formWordsEs}</legend>
          <label>
            {t.formTitle}
            <input value={words(loc).title} maxLength={120} required={loc === 'en-CA'} onChange={(e) => setWords(loc, 'title', e.target.value)} />
          </label>
          <label>
            {t.formObjective}
            <input value={words(loc).objective ?? ''} maxLength={300} onChange={(e) => setWords(loc, 'objective', e.target.value)} />
          </label>
          <label>
            {t.formPrize}
            <input value={words(loc).prize ?? ''} maxLength={120} onChange={(e) => setWords(loc, 'prize', e.target.value)} />
          </label>
        </fieldset>
      ))}

      {isMonth ? (
        <p className="photoroom__meta">{t.monthlyDatesNote}</p>
      ) : monthly ? (
        <>
          <label>
            {t.formTimezone}
            <input value={tz} onChange={(e) => setTz(e.target.value)} required />
          </label>
          <VoteLength value={voteDays} onChange={setVoteDays} />
        </>
      ) : (
        <>
          <div className="roomviewer__row">
            <label>
              {t.formEntriesOpen}
              <input type="date" value={startsOn} onChange={(e) => setStartsOn(e.target.value)} required />
            </label>
            <label>
              {t.formEntriesClose}
              <input type="date" value={endsOn} onChange={(e) => setEndsOn(e.target.value)} required />
            </label>
            {kind === 'vote' && (
              <label>
                {t.formVotingCloses}
                <input type="date" value={voteEndsOn} onChange={(e) => setVoteEndsOn(e.target.value)} required />
              </label>
            )}
          </div>
          <div className="roomviewer__row">
            <label>
              {t.formPhotosSince}
              <input type="date" value={photosSince} onChange={(e) => setPhotosSince(e.target.value)} required />
            </label>
            <label>
              {t.formTimezone}
              <input value={tz} onChange={(e) => setTz(e.target.value)} required />
            </label>
            <label>
              {t.formMaxEntries}
              <input type="number" min={1} max={10} value={maxEntries} onChange={(e) => setMaxEntries(Math.max(1, Math.min(10, Number(e.target.value) || 1)))} />
            </label>
          </div>
        </>
      )}

      {(kind === 'vote' || monthly) && (
        <label>
          {t.formVotesPerPerson}
          <input type="number" min={1} max={5} value={votesPerPerson} onChange={(e) => setVotesPerPerson(Math.max(1, Math.min(5, Number(e.target.value) || 1)))} />
        </label>
      )}

      <fieldset className="contests__places">
        <legend>{kind === 'vote' || monthly ? t.formPlaces : t.formJudgedPlaces}</legend>
        {starsList(t.pickPlace, placeStars, setPlaceStars)}
        <div className="roomviewer__row">
          {placeStars.length + awardStars.length < 10 && (
            <button type="button" className="btn btn--quiet btn--sm" onClick={() => setPlaceStars((cur) => [...cur, Math.round((cur[cur.length - 1] ?? 1000) / 2 / 50) * 50])}>
              {t.addPlace}
            </button>
          )}
          {placeStars.length > 1 && (
            <button type="button" className="btn btn--link" onClick={() => setPlaceStars((cur) => cur.slice(0, -1))}>
              {t.removePlace}
            </button>
          )}
        </div>
      </fieldset>

      {(kind === 'vote' || monthly) && (
        <fieldset className="contests__places">
          <legend>{t.formJudgesAwards}</legend>
          {starsList(t.pickJudgesAward, awardStars, setAwardStars)}
          <div className="roomviewer__row">
            {placeStars.length + awardStars.length < 10 && awardStars.length < 3 && (
              <button type="button" className="btn btn--quiet btn--sm" onClick={() => setAwardStars((cur) => [...cur, 2500])}>
                {t.addJudgesAward}
              </button>
            )}
            {awardStars.length > 0 && (
              <button type="button" className="btn btn--link" onClick={() => setAwardStars((cur) => cur.slice(0, -1))}>
                {t.removeJudgesAward}
              </button>
            )}
          </div>
        </fieldset>
      )}

      {/* 151: the winners in the feed — always for the monthly contest, a choice otherwise. */}
      {monthly || isMonth ? (
        <p className="photoroom__meta">{t.monthlyAnnounces}</p>
      ) : (
        <label className="contests__check">
          <input type="checkbox" checked={announce} onChange={(e) => setAnnounceOn(e.target.checked)} />
          <span>
            {t.announceInFeed}
            <span className="contests__hint">{t.announceHint}</span>
          </span>
        </label>
      )}

      <fieldset className="contests__places">
        <legend>{t.formJudges}</legend>
        <ul className="contests__judges">
          {judges.map((j) => (
            <li key={j.personId}>
              {j.name}{' '}
              <button
                type="button"
                className="btn btn--link"
                aria-label={t.removeJudge(j.name)}
                disabled={judges.length === 1}
                onClick={() => setJudges((cur) => cur.filter((x) => x.personId !== j.personId))}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
        <label>
          {t.judgeSearch}
          <input value={query} onChange={(e) => setQuery(e.target.value)} />
        </label>
        {found.length > 0 && (
          <div className="roomviewer__row">
            {found
              .filter((p) => !judges.some((j) => j.personId === p.personId))
              .map((p) => (
                <button
                  key={p.personId}
                  type="button"
                  className="btn btn--quiet btn--sm"
                  onClick={() => {
                    setJudges((cur) => [...cur, { personId: p.personId, name: p.displayName }])
                    setQuery('')
                  }}
                >
                  {t.addJudge(p.displayName)}
                </button>
              ))}
          </div>
        )}
      </fieldset>

      {error && (
        <p className="roles__error" role="alert">
          {error}
        </p>
      )}
      <div className="roomviewer__row">
        <button type="submit" className="btn btn--primary btn--sm" disabled={busy}>
          {t.save}
        </button>
        <button type="button" className="btn btn--quiet btn--sm" onClick={() => (onCancel ? onCancel() : void onDone())}>
          {t.cancel}
        </button>
      </div>
    </form>
  )
}

export function ConfirmResults({ contest: c, onDone }: { contest: Contest; onDone: () => Promise<void> }) {
  const lang = useHubLang()
  const t = CONTESTS[lang]
  const votePlaces = c.places.filter((p) => p.award === 'place')
  const awardPlaces = c.places.filter((p) => p.award === 'judges')
  // EVERY qualified photo, not the gallery's first page: the order of the
  // count is the whole question here (rule 1).
  const [all, setAll] = useState<GalleryEntry[] | null>(null)
  const [placed, setPlaced] = useState<string[]>([])
  const [awards, setAwards] = useState<Record<number, string>>({})
  const [picks, setPicks] = useState<Record<number, string>>({})
  const [confirming, setConfirming] = useState(false)
  const [results, setResults] = useState<ConfirmOutcome[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let live = true
    ;(async () => {
      try {
        const out: GalleryEntry[] = []
        for (let offset = 0; ; offset += 100) {
          const page = await loadGallery(c.id, offset, 100)
          out.push(...page.entries)
          if (out.length >= page.total || page.entries.length === 0) break
        }
        if (!live) return
        const qualified = out.filter((e) => !e.disqualified)
        setAll(qualified)
        if (c.kind === 'vote') setPlaced(proposeVotePlaces(qualified, votePlaces.length).placed)
      } catch (e) {
        if (live) setError(messageOf(e))
      }
    })()
    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c.id])

  if (results) {
    return (
      <div className="contests__results" role="status">
        <h3 className="roomviewer__h">{t.confirmHeading}</h3>
        <ul>
          {results.map((r) => (
            <li key={r.place}>
              {t.resultLine(labelOf(r.place), nameOf(r.path), r.stars, r.told)}
              {r.outcome !== 'awarded' ? ` — ${t.noStars(r.outcome)}` : ''}
            </li>
          ))}
        </ul>
        <button type="button" className="btn btn--primary btn--sm" onClick={() => void onDone()}>
          {t.winnersCta}
        </button>
      </div>
    )
  }

  if (!all) return error ? <p className="roles__error" role="alert">{error}</p> : <p className="queue__empty">{t.loading}</p>

  const votesOf = (path: string) => all.find((e) => e.path === path)?.votes ?? 0
  const nameOf = (path: string) => all.find((e) => e.path === path)?.entrantName ?? ''
  const mine = (path: string) => all.find((e) => e.path === path)?.isMine === true
  function labelOf(place: number) {
    const p = c.places.find((x) => x.place === place)
    if (p?.award === 'judges') return t.judgesAward(place - votePlaces.length, awardPlaces.length > 1)
    return t.placeName(place)
  }

  const winners: { place: number; path: string }[] =
    c.kind === 'vote'
      ? [
          ...placed.map((path, i) => ({ place: i + 1, path })),
          ...Object.entries(awards).filter(([, p]) => !!p).map(([pl, path]) => ({ place: Number(pl), path })),
        ]
      : Object.entries(picks).filter(([, p]) => !!p).map(([pl, path]) => ({ place: Number(pl), path }))
  const unplaced = all.filter((e) => !placed.includes(e.path))

  async function confirm() {
    setBusy(true)
    setError(null)
    try {
      setResults(await confirmResults(c.id, winners.sort((a, b) => a.place - b.place)))
      setConfirming(false)
    } catch (e) {
      setError(messageOf(e))
      setConfirming(false)
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="contests__confirm">
      <h3 className="roomviewer__h">{t.confirmHeading}</h3>
      <p className="photoroom__meta">{c.kind === 'vote' ? t.confirmLedeVote : t.confirmLedeJudged}</p>
      {error && (
        <p className="roles__error" role="alert">
          {error}
        </p>
      )}

      {c.kind === 'vote' ? (
        <>
          {placed.length === 0 && <p className="photoroom__meta">{t.noVotes}</p>}
          <ol className="contests__ranked">
            {placed.map((path, i) => {
              const levelAbove = i > 0 && votesOf(placed[i - 1]) === votesOf(path)
              const levelOut = unplaced.some((e) => (e.votes ?? 0) === votesOf(path))
              return (
                <li key={path}>
                  <RoomThumb path={path} className="contests__thumb" />
                  <span>
                    <strong>{t.placeName(i + 1)}</strong> — {nameOf(path)} · {t.votes(votesOf(path))}
                    {(levelAbove || levelOut) && <em className="photoroom__meta"> {t.tiedNote}</em>}
                    {mine(path) && (levelAbove || levelOut) && <span className="photoroom__meta"> {t.ownPhoto}</span>}
                  </span>
                  {levelAbove && (
                    <button type="button" className="btn btn--link" onClick={() => setPlaced((cur) => swapIfTied(cur, i, i - 1, votesOf))}>
                      {t.moveUp}
                    </button>
                  )}
                  {levelOut && (
                    <select
                      aria-label={t.tiedNote}
                      value={path}
                      onChange={(e) => setPlaced((cur) => cur.map((p, j) => (j === i ? e.target.value : p)))}
                    >
                      <option value={path}>{nameOf(path)}</option>
                      {unplaced
                        .filter((u) => (u.votes ?? 0) === votesOf(path))
                        .map((u) => (
                          <option key={u.path} value={u.path}>
                            {u.entrantName}
                          </option>
                        ))}
                    </select>
                  )}
                </li>
              )
            })}
          </ol>
          {awardPlaces.map((p, i) => (
            <label key={p.place} className="contests__pick">
              {t.pickJudgesAward(i + 1)}
              <select value={awards[p.place] ?? ''} onChange={(e) => setAwards((cur) => ({ ...cur, [p.place]: e.target.value }))}>
                <option value="">{t.nobody}</option>
                {unplaced
                  .filter((u) => !u.isMine && !Object.entries(awards).some(([pl, path]) => Number(pl) !== p.place && path === u.path))
                  .map((u) => (
                    <option key={u.path} value={u.path}>
                      {u.entrantName} · {t.votes(u.votes ?? 0)}
                    </option>
                  ))}
              </select>
            </label>
          ))}
        </>
      ) : (
        votePlaces.map((p) => (
          <label key={p.place} className="contests__pick">
            {t.pickPlace(p.place)}
            <select value={picks[p.place] ?? ''} onChange={(e) => setPicks((cur) => ({ ...cur, [p.place]: e.target.value }))}>
              <option value="">{t.nobody}</option>
              {all
                .filter((u) => !u.isMine && !Object.entries(picks).some(([pl, path]) => Number(pl) !== p.place && path === u.path))
                .map((u) => (
                  <option key={u.path} value={u.path}>
                    {u.entrantName}
                  </option>
                ))}
            </select>
          </label>
        ))
      )}

      <div className="roomviewer__row">
        <button type="button" className="btn btn--primary btn--sm" disabled={busy} onClick={() => setConfirming(true)}>
          {t.confirmCta}
        </button>
      </div>

      {confirming && (
        <Dialog open title={t.confirmHeading} onClose={() => setConfirming(false)}>
          <p>{t.confirmFinal}</p>
          <ul>
            {winners
              .sort((a, b) => a.place - b.place)
              .map((w) => (
                <li key={w.place}>
                  {labelOf(w.place)} — {nameOf(w.path)} · {(c.places.find((p) => p.place === w.place)?.stars ?? 0).toLocaleString()}
                </li>
              ))}
          </ul>
          <div className="roomviewer__row">
            <button type="button" className="btn btn--primary btn--sm" disabled={busy} onClick={() => void confirm()}>
              {t.confirmButton}
            </button>
            <button type="button" className="btn btn--quiet btn--sm" onClick={() => setConfirming(false)}>
              {t.cancel}
            </button>
          </div>
        </Dialog>
      )}
    </section>
  )
}

/**
 * 151. How long the monthly contest's vote runs: the whole next month (null,
 * the default) or only its first N days (1-28).
 */
export function VoteLength({ value, onChange }: { value: number | null; onChange: (v: number | null) => void }) {
  const t = CONTESTS[useHubLang()]
  const name = useId()
  return (
    <fieldset className="contests__places votelength">
      <legend>{t.votingRuns}</legend>
      <label className="contests__pick">
        <input type="radio" name={name} checked={value === null} onChange={() => onChange(null)} /> {t.voteWholeMonth}
      </label>
      <label className="contests__pick">
        <input type="radio" name={name} checked={value !== null} onChange={() => onChange(value ?? 7)} /> {t.voteFirstDays}
        {value !== null && (
          <>
            <input
              className="votelength__n"
              type="number"
              min={1}
              max={28}
              value={value}
              aria-label={t.daysUnit}
              onChange={(e) => onChange(Math.max(1, Math.min(28, Number(e.target.value) || 1)))}
            />
            {t.daysUnit}
          </>
        )}
      </label>
    </fieldset>
  )
}
